package cz.vojtechhoranek.site.localization

import androidx.compose.runtime.staticCompositionLocalOf

private const val MastersThesisUrl = "https://dspace.cvut.cz/entities/publication/51ea2fe6-734b-4ee8-a5d2-8f60dc1aeb53"
private const val BachelorsThesisUrl = "https://dspace.cvut.cz/entities/publication/c5afd69f-2d29-45db-9004-095b67c06cd9"
private const val SentiaryUrl = "https://sentiary.com/"
private const val TakeYourPillUrl = "https://github.com/vojta-horanek/take-your-pill"

private const val KotlinTalkSlidesUrl = "https://kug.vojtechhoranek.cz/"
private const val KotlinTalkRecordingUrl = "https://www.youtube.com/watch?v=Mij1N6F75Qs"

data class EntryLink(val text: String, val url: String)

enum class ProjectGalleryKind { Rovinka, TakeYourPill }

data class TimelineEntry(
    val date: String,
    val kind: String,
    val title: String,
    val organization: String,
    val description: String,
    val organizationUrl: String? = null,
    val honors: String? = null,
    val gallery: ProjectGalleryKind? = null,
    val links: List<EntryLink> = emptyList(),
)

private val englishTimelineEntries = listOf(
    TimelineEntry(
        date = "Jan 2022 – present",
        kind = "Work",
        title = "Mobile app developer",
        organization = "Matee",
        description = "I develop mobile apps and more with Kotlin Multiplatform. I joined Matee as early as my first year at FIT CTU.",
    ),
    TimelineEntry(
        date = "2024 – 2026",
        kind = "Education",
        title = "Master’s in Software Engineering · Ing.",
        organization = "Faculty of Information Technology, CTU in Prague",
        description = "For my thesis, I built Rovinka with the Regional Hospital in Liberec. It includes a patient app and a web interface for spinal unit staff.",
        honors = "Graduated with honors · Dean’s Award",
        links = listOf(EntryLink("Read my master’s thesis", MastersThesisUrl)),
        gallery = ProjectGalleryKind.Rovinka,
    ),
    TimelineEntry(
        date = "2021 – 2024",
        kind = "Education",
        title = "Bachelor’s in Software Engineering · Bc.",
        organization = "Faculty of Information Technology, CTU in Prague",
        description = "My thesis focused on a localization platform for mobile apps. Its current version is live as Sentiary.",
        honors = "Graduated with honors",
        links = listOf(
            EntryLink("Read my bachelor’s thesis", BachelorsThesisUrl),
            EntryLink("Visit Sentiary", SentiaryUrl),
        ),
    ),
    TimelineEntry(
        date = "2017 – 2021",
        kind = "Education",
        title = "Information Technology · Programming",
        organization = "SPŠE a gymnázium V Úžlabině",
        description = "I completed my secondary education with a graduation project: Take Your Pill, an Android app for medication reminders.",
        links = listOf(EntryLink("View Take Your Pill on GitHub", TakeYourPillUrl)),
        gallery = ProjectGalleryKind.TakeYourPill,
    ),
)

data class SiteText(
    val languageTag: String,
    val about: String,
    val introduction: String,
    val contact: String,
    val workAndEducation: String,
    val outsideWork: String,
    val outsideWorkEntries: List<TimelineEntry>,
    val builtWith: String,
    val description: String,
    val timeline: List<TimelineEntry>,
    val talksHeading: String,
    val talks: List<TimelineEntry>,
)

private val czechTimelineEntries = listOf(
    TimelineEntry(
        date = "leden 2022 – dosud",
        kind = "Práce",
        title = "Vývojář mobilních aplikací",
        organization = "Matee",
        description = "Vyvíjím (nejen) mobilní aplikace v Kotlin Multiplatform. Do Matee jsem nastoupil již během prvního ročníku na FIT ČVUT.",
    ),
    TimelineEntry(
        date = "2024 – 2026",
        kind = "Vzdělání",
        title = "Softwarové inženýrství · Ing.",
        organization = "Fakulta informačních technologií ČVUT v Praze",
        description = "V rámci diplomové práce jsem ve spolupráci s Krajskou nemocnicí Liberec vytvořil platformu Rovinka. Zahrnuje aplikaci pro pacienty a webové rozhraní pro personál spinálního oddělení.",
        honors = "Červený diplom · Cena děkana",
        links = listOf(EntryLink("Přečíst diplomovou práci", MastersThesisUrl)),
        gallery = ProjectGalleryKind.Rovinka,
    ),
    TimelineEntry(
        date = "2021 – 2024",
        kind = "Vzdělání",
        title = "Softwarové inženýrství · Bc.",
        organization = "Fakulta informačních technologií ČVUT v Praze",
        description = "V bakalářské práci jsem vytvořil lokalizační platformu pro mobilní aplikace. Její aktuální verze je dostupná pod názvem Sentiary.",
        honors = "Červený diplom",
        links = listOf(
            EntryLink("Přečíst bakalářskou práci", BachelorsThesisUrl),
            EntryLink("Navštívit Sentiary", SentiaryUrl),
        ),
    ),
    TimelineEntry(
        date = "2017 – 2021",
        kind = "Vzdělání",
        title = "Informační technologie · Programování",
        organization = "SPŠE a gymnázium V Úžlabině",
        description = "Jako maturitní projekt jsem vytvořil Take Your Pill, aplikaci pro Android, která připomíná užívání léků.",
        links = listOf(EntryLink("Take Your Pill na GitHubu", TakeYourPillUrl)),
        gallery = ProjectGalleryKind.TakeYourPill,
    ),
)

val EnglishText = SiteText(
    languageTag = "en",
    about = "About me",
    introduction = "Mobile app developer at Matee and freelancer. Software engineering graduate of FIT CTU.",
    contact = "Contact",
    workAndEducation = "Work & education",
    outsideWork = "Outside work",
    outsideWorkEntries = listOf(
        TimelineEntry(
            date = "Sep 2015 – present",
            kind = "Volunteering",
            title = "Junák – český skaut",
            organization = "VJAS Brandýs nad Labem",
            organizationUrl = "https://vjas.skauting.cz/",
            description = "Deputy leader of the Benjamínci troop",
        ),
    ),
    builtWith = "Built with ",
    description = "Freelance mobile app developer. Work, education, and projects by Vojtěch Hořánek.",
    timeline = englishTimelineEntries,
    talksHeading = "Talks",
    talks = listOf(
        TimelineEntry(
            date = "Apr 1, 2025",
            kind = "Talk",
            title = "Full-stack development in Kotlin",
            organization = "Kotlin Czech User Group · Productboard, Prague",
            description = "At the Kotlin Meetup, I showed how to build across platforms with Kotlin Multiplatform. The talk covered Compose HTML, Kobweb, Ktor, and Firebase.",
            links = listOf(
                EntryLink("View the presentation", KotlinTalkSlidesUrl),
                EntryLink("Watch the recording", KotlinTalkRecordingUrl),
            ),
        ),
    ),
)

val CzechText = SiteText(
    languageTag = "cs",
    about = "O mně",
    introduction = "Vyvíjím mobilní aplikace v Matee i na volné noze. Vystudoval jsem softwarové inženýrství na FIT ČVUT.",
    contact = "Kontakt",
    workAndEducation = "Práce a vzdělání",
    outsideWork = "Mimo práci",
    outsideWorkEntries = listOf(
        TimelineEntry(
            date = "září 2015 – dosud",
            kind = "Dobrovolnictví",
            title = "Junák – český skaut",
            organization = "VJAS Brandýs nad Labem",
            organizationUrl = "https://vjas.skauting.cz/",
            description = "Zástupce vedoucího oddílu Benjamínků",
        ),
    ),
    builtWith = "Vytvořeno pomocí ",
    description = "Vývojář mobilních aplikací na volné noze. Práce, vzdělání a projekty Vojtěcha Hořánka.",
    timeline = czechTimelineEntries,
    talksHeading = "Přednášky",
    talks = listOf(
        TimelineEntry(
            date = "1. dubna 2025",
            kind = "Přednáška",
            title = "Full-stack vývoj v Kotlinu",
            organization = "Kotlin Czech User Group · Productboard, Praha",
            description = "Na Kotlin Meetupu jsem ukázal vývoj napříč platformami pomocí Kotlin Multiplatform. Představil jsem Compose HTML, Kobweb, Ktor a Firebase.",
            links = listOf(
                EntryLink("Prohlédnout prezentaci", KotlinTalkSlidesUrl),
                EntryLink("Přehrát záznam", KotlinTalkRecordingUrl),
            ),
        ),
    ),
)

val LocalSiteText = staticCompositionLocalOf { EnglishText }
